/**
 * Binary game packets (peer ↔ peer over the WebRTC DataChannel, or relayed verbatim by the server over the
 * WebSocket). Unreliable and unordered: every INPUT packet carries redundant history, so any single packet can be
 * lost. All multi-byte fields are LITTLE-ENDIAN. Ticks are uint32. `NO_TICK` (0xFFFFFFFF) means "none yet".
 *
 * Layouts (byte offsets):
 *
 *   INPUT (type 1)  — the sender's OWN inputs for ticks [startTick, startTick + count)
 *     0  u8   type = 1
 *     1  u32  ackTick         highest remote tick T such that ALL remote inputs ≤ T have been received (NO_TICK = none)
 *     5  u32  startTick       tick of inputs[0]
 *     9  u8   count           0..MAX_INPUTS_PER_PACKET (0 = ack-only keepalive)
 *    10  i8   frameAdvantage  sender's local frame advantage (see below), clamped to [-128,127], for time sync
 *    11  u32  senderTick      the sender's current simulated tick (state.tick of its `current`) when it sent this
 *    15  u8   inputDelay      the sender's current input delay in ticks (0..MAX_INPUT_DELAY)
 *    16  u8[count] inputs     InputFrame bytes
 *
 *   Time sync (GGPO-style). Each peer computes its LOCAL advantage
 *       localAdv = localTick − estRemoteTick,   estRemoteTick = remote senderTick + (rtt/2 in ticks)
 *   using the newest INPUT packet's `senderTick` (not its input ticks, which are offset by the remote's delay),
 *   and sends it as `frameAdvantage`. The session then uses (localAdv − remoteAdv) / 2: when it is > 0 the local
 *   peer is ahead and inserts wait frames (≤ 1 per NETCODE_DEFAULTS.timeSyncWaitEvery ticks).
 *   Input delay is per peer and may differ between the two peers; `inputDelay` lets the receiver tell a delay
 *   change from loss. Changing the delay never rewrites already-sent ticks: INCREASING it by k repeats the last
 *   sent input for k extra ticks; DECREASING it by k skips k ticks of sampled input (they are never sent).
 *   Tick numbering of sent inputs stays strictly contiguous either way.
 *
 *   PING (type 2)
 *     0 u8 type = 2 · 1 u32 id · 5 f64 sendTimeMs (sender's performance.now())
 *
 *   PONG (type 3) — echo of a PING
 *     0 u8 type = 3 · 1 u32 id · 5 f64 echoTimeMs (the PING's sendTimeMs)
 *
 *   HASH (type 4) — desync detection, sent for confirmed ticks every HASH_INTERVAL_TICKS
 *     0 u8 type = 4 · 1 u32 tick · 5 u32 hash
 *
 *   HELLO (type 5) — first packet on a fresh transport; both sides must agree before inputs flow
 *     0 u8 type = 5 · 1 u16 protocol · 3 u8 playerIndex · 4 u32 matchKey · 8 u32 contentHash (SIM_CONTENT_HASH).
 *     matchKey = FNV-1a over the matchId string followed by the hash of the match's initial state
 *     (`@hf/netcode` matchKeyFor), so peers must agree on the build, the match AND its config (seed…); the byte
 *     layout is unchanged and the server never inspects it. `matchKeyOf` below is the matchId-only part. A peer
 *     whose protocol, matchKey or contentHash differs sends QUIT reason VERSION and closes: never start a match
 *     between different sim builds.
 *
 *   QUIT (type 6) — graceful leave
 *     0 u8 type = 6 · 1 u8 reason (QuitReason)
 *
 *   HASH_DETAIL (type 7) — desync diagnostics, sent once by each peer after a HASH mismatch
 *     0 u8 type = 7 · 1 u32 tick · 5 u32[HASH_PART_COUNT] parts (`@hf/sim` hashStateParts, sections in
 *     HASH_PART_NAMES order). Comparing the parts localizes the divergence (prng / player 0 / player 1 / ball /
 *     rules / camera); each side also dumps its snapshot of that tick (JSON) to the console for offline diffing.
 *
 * `@hf/netcode` owns the semantics; this module only owns the byte layout so the client, netcode and tests agree.
 */
import { Fnv1a } from './hash';
import type { InputFrame } from './input';
import { INPUT_MASK } from './input';

export const PacketType = { INPUT: 1, PING: 2, PONG: 3, HASH: 4, HELLO: 5, QUIT: 6, HASH_DETAIL: 7 } as const;
export type PacketType = (typeof PacketType)[keyof typeof PacketType];

export const QuitReason = { QUIT: 0, DESYNC: 1, ERROR: 2, VERSION: 3 } as const;
export type QuitReason = (typeof QuitReason)[keyof typeof QuitReason];

export const NO_TICK = 0xffffffff;
export const MAX_INPUTS_PER_PACKET = 64;
export const INPUT_HEADER_BYTES = 16;
export const HASH_INTERVAL_TICKS = 30;
/** Upper bound of a peer's input delay (ticks); also bounds the INPUT header's `inputDelay`. */
export const MAX_INPUT_DELAY = 6;
/** State sections of `hashStateParts` / HASH_DETAIL, in order. */
export const HASH_PART_NAMES = ['prng', 'player0', 'player1', 'ball', 'rules', 'camera'] as const;
export const HASH_PART_COUNT = HASH_PART_NAMES.length;
const HASH_DETAIL_BYTES = 5 + 4 * HASH_PART_COUNT;

export interface InputPacket {
  type: typeof PacketType.INPUT;
  ackTick: number;
  startTick: number;
  frameAdvantage: number;
  senderTick: number;
  inputDelay: number;
  inputs: InputFrame[];
}
export interface PingPacket {
  type: typeof PacketType.PING;
  id: number;
  timeMs: number;
}
export interface PongPacket {
  type: typeof PacketType.PONG;
  id: number;
  timeMs: number;
}
export interface HashPacket {
  type: typeof PacketType.HASH;
  tick: number;
  hash: number;
}
export interface HelloPacket {
  type: typeof PacketType.HELLO;
  protocol: number;
  playerIndex: number;
  matchKey: number;
  contentHash: number;
}
export interface QuitPacket {
  type: typeof PacketType.QUIT;
  reason: QuitReason;
}
export interface HashDetailPacket {
  type: typeof PacketType.HASH_DETAIL;
  tick: number;
  /** HASH_PART_COUNT u32 section hashes. */
  parts: number[];
}
export type GamePacket =
  InputPacket | PingPacket | PongPacket | HashPacket | HelloPacket | QuitPacket | HashDetailPacket;

/** Stable 32-bit key for a matchId (HELLO packets). */
export const matchKeyOf = (matchId: string): number => new Fnv1a().str(matchId).digest();

const clampI8 = (v: number): number => (v < -128 ? -128 : v > 127 ? 127 : Math.trunc(v));

export const encodePacket = (p: GamePacket): Uint8Array => {
  switch (p.type) {
    case PacketType.INPUT: {
      const n = p.inputs.length;
      if (n > MAX_INPUTS_PER_PACKET) throw new RangeError(`too many inputs: ${n}`);
      const buf = new Uint8Array(INPUT_HEADER_BYTES + n);
      const dv = new DataView(buf.buffer);
      dv.setUint8(0, p.type);
      dv.setUint32(1, p.ackTick >>> 0, true);
      dv.setUint32(5, p.startTick >>> 0, true);
      dv.setUint8(9, n);
      dv.setInt8(10, clampI8(p.frameAdvantage));
      dv.setUint32(11, p.senderTick >>> 0, true);
      dv.setUint8(15, Math.min(Math.max(p.inputDelay | 0, 0), MAX_INPUT_DELAY));
      for (let i = 0; i < n; i++) buf[INPUT_HEADER_BYTES + i] = p.inputs[i]! & INPUT_MASK;
      return buf;
    }
    case PacketType.PING:
    case PacketType.PONG: {
      const buf = new Uint8Array(13);
      const dv = new DataView(buf.buffer);
      dv.setUint8(0, p.type);
      dv.setUint32(1, p.id >>> 0, true);
      dv.setFloat64(5, p.timeMs, true);
      return buf;
    }
    case PacketType.HASH: {
      const buf = new Uint8Array(9);
      const dv = new DataView(buf.buffer);
      dv.setUint8(0, p.type);
      dv.setUint32(1, p.tick >>> 0, true);
      dv.setUint32(5, p.hash >>> 0, true);
      return buf;
    }
    case PacketType.HELLO: {
      const buf = new Uint8Array(12);
      const dv = new DataView(buf.buffer);
      dv.setUint8(0, p.type);
      dv.setUint16(1, p.protocol, true);
      dv.setUint8(3, p.playerIndex);
      dv.setUint32(4, p.matchKey >>> 0, true);
      dv.setUint32(8, p.contentHash >>> 0, true);
      return buf;
    }
    case PacketType.QUIT: {
      return Uint8Array.of(p.type, p.reason);
    }
    case PacketType.HASH_DETAIL: {
      if (p.parts.length !== HASH_PART_COUNT) throw new RangeError(`expected ${HASH_PART_COUNT} hash parts`);
      const buf = new Uint8Array(HASH_DETAIL_BYTES);
      const dv = new DataView(buf.buffer);
      dv.setUint8(0, p.type);
      dv.setUint32(1, p.tick >>> 0, true);
      for (let i = 0; i < HASH_PART_COUNT; i++) dv.setUint32(5 + 4 * i, p.parts[i]! >>> 0, true);
      return buf;
    }
  }
};

/** Decode a packet; returns null for malformed/unknown data (never throws). */
export const decodePacket = (buf: Uint8Array): GamePacket | null => {
  if (buf.byteLength < 1) return null;
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const type = dv.getUint8(0);
  switch (type) {
    case PacketType.INPUT: {
      if (buf.byteLength < INPUT_HEADER_BYTES) return null;
      const count = dv.getUint8(9);
      if (count > MAX_INPUTS_PER_PACKET || buf.byteLength !== INPUT_HEADER_BYTES + count) return null;
      const inputs: InputFrame[] = new Array<InputFrame>(count);
      for (let i = 0; i < count; i++) inputs[i] = buf[INPUT_HEADER_BYTES + i]! & INPUT_MASK;
      return {
        type,
        ackTick: dv.getUint32(1, true),
        startTick: dv.getUint32(5, true),
        frameAdvantage: dv.getInt8(10),
        senderTick: dv.getUint32(11, true),
        inputDelay: dv.getUint8(15),
        inputs,
      };
    }
    case PacketType.PING:
    case PacketType.PONG:
      if (buf.byteLength !== 13) return null;
      return { type, id: dv.getUint32(1, true), timeMs: dv.getFloat64(5, true) };
    case PacketType.HASH:
      if (buf.byteLength !== 9) return null;
      return { type, tick: dv.getUint32(1, true), hash: dv.getUint32(5, true) };
    case PacketType.HELLO:
      if (buf.byteLength !== 12) return null;
      return {
        type,
        protocol: dv.getUint16(1, true),
        playerIndex: dv.getUint8(3),
        matchKey: dv.getUint32(4, true),
        contentHash: dv.getUint32(8, true),
      };
    case PacketType.QUIT: {
      if (buf.byteLength !== 2) return null;
      const reason = dv.getUint8(1);
      if (reason > QuitReason.VERSION) return null;
      return { type, reason: reason as QuitReason };
    }
    case PacketType.HASH_DETAIL: {
      if (buf.byteLength !== HASH_DETAIL_BYTES) return null;
      const parts = new Array<number>(HASH_PART_COUNT);
      for (let i = 0; i < HASH_PART_COUNT; i++) parts[i] = dv.getUint32(5 + 4 * i, true);
      return { type, tick: dv.getUint32(1, true), parts };
    }
    default:
      return null;
  }
};
